import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Send, MessageSquare, Loader2, Paperclip, File as FileIcon, Image as ImageIcon, Trash2 } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { getGenerativeModel } from 'firebase/ai';
import { ai, db, auth } from '@/firebase/config';
import { getModelForFeature } from '@/services/root_utils/aiConfigService';
import { collection, query, getDocs } from 'firebase/firestore';
import { ExamPaper, Question } from './types';
import { regenerateQuestion } from '../services/geminiService';

interface AIChatAssistantProps {
  onClose: () => void;
  context?: string; // Optional context, like the current exam paper title
  examPaper?: ExamPaper | null;
  onUpdateExamPaper?: (newPaper: ExamPaper) => void;
}

export function AIChatAssistant({ onClose, context, examPaper, onUpdateExamPaper }: AIChatAssistantProps) {
  const [messages, setMessages] = useState<{ role: 'user' | 'ai'; content: string }[]>(() => {
    if (examPaper && examPaper.sections.length > 0) {
      return [{ role: 'ai', content: `I see you are working on ${context || 'an assessment'}. I've read through the generated questions and I'm ready to help you improve them. What would you like to change?` }];
    }
    return [{ role: 'ai', content: "Hi! I'm your AI Assessment Assistant. I can help you brainstorm questions, review your tuning, or discuss assessment strategies. What's on your mind?" }];
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [subjectsContext, setSubjectsContext] = useState<string>('Loading subjects...');
  const [attachments, setAttachments] = useState<{file: File, type: 'image' | 'pdf', dataUrl?: string}[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getFlatQuestions = (paper: ExamPaper) => paper.sections.flatMap((section, sectionIndex) =>
    section.questions.map((question, questionIndex) => ({ sectionIndex, questionIndex, question }))
  );

  const applyQuestionUpdate = (sectionIndex: number, questionIndex: number, question: Question) => {
    if (!examPaper || !onUpdateExamPaper) return;
    const nextPaper: ExamPaper = {
      ...examPaper,
      sections: examPaper.sections.map((section, sIndex) => ({
        ...section,
        questions: section.questions.map((existing, qIndex) =>
          sIndex === sectionIndex && qIndex === questionIndex ? question : existing
        ),
      })),
    };
    onUpdateExamPaper(nextPaper);
  };

  const tryHandleDirectEdit = async (userMsg: string) => {
    if (!examPaper || !onUpdateExamPaper) return false;

    const regenMatch = userMsg.match(/\b(?:regenerate|rewrite|replace|redo|make\s+new)\b[\s\S]*?\b(?:question|q)\s*#?\s*(\d+)\b/i);
    if (regenMatch) {
      const displayNumber = Number(regenMatch[1]);
      const target = getFlatQuestions(examPaper)[displayNumber - 1];
      if (!target) {
        setMessages(prev => [...prev, { role: 'ai', content: `I could not find question ${displayNumber} in the current assessment.` }]);
        return true;
      }

      const updated = await regenerateQuestion({
        originalQuestion: target.question,
        instructions: `${userMsg}\nKeep the result connected to any diagram if the original or requested question uses one. Return only the replacement question object.`,
        subject: context,
      });
      applyQuestionUpdate(target.sectionIndex, target.questionIndex, { ...target.question, ...updated });
      setMessages(prev => [...prev, { role: 'ai', content: `Done. I regenerated question ${displayNumber} directly in the assessment.` }]);
      return true;
    }

    return false;
  };

  const applyEditBlock = (aiReply: string) => {
    if (!onUpdateExamPaper || !examPaper) return aiReply;
    const patterns = [
      /```json:edit\s*([\s\S]*?)```/i,
      /```json\s*([\s\S]*?)```/i,
    ];

    for (const pattern of patterns) {
      const match = aiReply.match(pattern);
      if (!match) continue;
      try {
        const parsed = JSON.parse(match[1]);
        const nextPaper = parsed.sections
          ? { ...examPaper, sections: parsed.sections }
          : parsed.title && parsed.sections
            ? parsed
            : null;
        if (nextPaper?.sections) {
          onUpdateExamPaper(nextPaper);
          return aiReply.replace(pattern, "\n*[I applied these changes to the assessment.]*\n");
        }
      } catch (err) {
        console.error("Failed to parse AI edit JSON:", err);
      }
    }

    return aiReply;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const newAttachments = files.map(file => {
      const type = file.type.startsWith('image/') ? 'image' : 'pdf';
      const attachment: {file: File, type: 'image'|'pdf', dataUrl?: string} = { file, type };
      
      if (type === 'image') {
        const reader = new FileReader();
        reader.onload = (e) => {
          setAttachments(prev => prev.map(a => a.file === file ? { ...a, dataUrl: e.target?.result as string } : a));
        };
        reader.readAsDataURL(file);
      }
      return attachment;
    });
    
    setAttachments(prev => [...prev, ...newAttachments]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!auth.currentUser) return;
      try {
        const q = query(collection(db, `teaching_workspaces/${auth.currentUser.uid}/styleProfiles`));
        const snapshot = await getDocs(q);
        const styles = snapshot.docs.map(d => d.data());
        const subjects = [...new Set(styles.map(s => s.subject).filter(Boolean))];
        
        if (subjects.length > 0) {
          setSubjectsContext(`The teacher currently has AI tuning configured for ${subjects.length} subjects: ${subjects.join(', ')}.`);
        } else {
          setSubjectsContext(`The teacher currently has no subjects configured for AI tuning.`);
        }
      } catch (err) {
        console.error("Failed to load subjects for chat context", err);
      }
    };
    fetchSubjects();
  }, []);

  const handleSend = async () => {
    if ((!input.trim() && attachments.length === 0) || loading) return;
    
    const userMsg = input.trim();
    setInput('');
    const currentAttachments = [...attachments];
    setAttachments([]);
    
    let displayMsg = userMsg;
    if (currentAttachments.length > 0) {
      displayMsg += `\n*[Attached ${currentAttachments.length} file(s)]*`;
    }
    
    setMessages(prev => [...prev, { role: 'user', content: displayMsg || '*Sent attachment(s)*' }]);
    setLoading(true);

    try {
      if (await tryHandleDirectEdit(userMsg)) {
        return;
      }

      const modelName = await getModelForFeature('independent_examGeneration');
      const model = getGenerativeModel(ai, { model: modelName });
      
      const systemPrompt = `You are an expert AI Assessment Assistant helping a teacher. 
${context ? `Context: The teacher is currently working on: ${context}\n` : ''}

KNOWLEDGE BASE:
- The teacher can upload their own past exam questions so the AI learns their exact teaching style and manner.
- This tuning is tied directly to a specific "Subject".
- The teacher can add new subjects, edit existing subject tunings, or delete them via the "AI Style Tuner".
- CURRENT STATE: ${subjectsContext}

EXAM EDITING ABILITY:
You have the ability to directly modify the exam paper based on the teacher's requests.
You already have the full current paper below. NEVER ask the teacher to paste a question number or repeat the paper.
If the teacher asks you to edit, regenerate, replace, add, remove, reorder, simplify, make harder, add diagrams, remove diagrams, or rewrite anything in the assessment, you MUST output the updated 'sections' array inside a JSON block with the language identifier \`json:edit\`.
For example:
\`\`\`json:edit
{
  "sections": [
    ... updated sections array ...
  ]
}
\`\`\`
IMPORTANT: When editing, always output the full, complete sections array with all questions, even the ones you didn't change, because your output will completely replace the current state.
After the JSON block, include only one short sentence summarising what changed.
For diagram questions, make the wording depend on the diagram ("Study the graph below...", "Use the figure below...") and include usable diagram data, not an empty coordinate plane.
Here is the current exam paper state:
\`\`\`json
${examPaper ? JSON.stringify(examPaper.sections, null, 2) : 'No exam loaded'}
\`\`\`

If the teacher asks about how to tune the AI, their subjects, or how the system learns, use the knowledge above to inform them.
Be concise, practical, and highly professional.`;

      let pdfText = "";
      const pdfAttachments = currentAttachments.filter(a => a.type === 'pdf');
      if (pdfAttachments.length > 0) {
        const { extractTextFromPDF } = await import('../services/documentService');
        for (const pdf of pdfAttachments) {
          try {
            const result = await extractTextFromPDF(pdf.file, () => {});
            pdfText += `\n\n--- ATTACHED PDF: ${pdf.file.name} ---\n${result.text}\n--- END OF PDF ---\n`;
          } catch (e) {
            console.error("PDF Extraction failed", e);
          }
        }
      }

      const imageParts = currentAttachments
        .filter(a => a.type === 'image' && a.dataUrl)
        .map(a => {
          const base64 = a.dataUrl!.split(',')[1];
          return {
            inlineData: {
              data: base64,
              mimeType: a.file.type
            }
          };
        });

      const parts: any[] = [
        { text: `${systemPrompt}\n\nTeacher: ${userMsg}${pdfText}` },
        ...imageParts
      ];

      const result = await model.generateContent(parts);
      let aiReply = result.response.text();
      
      aiReply = applyEditBlock(aiReply);

      setMessages(prev => [...prev, { role: 'ai', content: aiReply }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'ai', content: 'Sorry, I could not process that. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-white dark:bg-[#0f172a] animate-in fade-in duration-200">
      {/* Chat header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200/60 dark:border-white/10 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl shrink-0">
        <button
          onClick={onClose}
          className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
            <MessageSquare className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <span className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-200 block">AI Assistant</span>
            <span className="text-[10px] text-slate-400">Always here to help</span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-blue-600 text-white rounded-br-md shadow-md shadow-blue-500/10'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-bl-md border border-slate-200/60 dark:border-white/5 shadow-sm'
            }`}>
              <div className="markdown-body text-xs prose dark:prose-invert max-w-none">
                {msg.role === 'ai' ? (
                  <div dangerouslySetInnerHTML={{ __html: msg.content.replace(/\n/g, '<br/>') }} />
                ) : (
                  msg.content
                )}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-bl-md px-5 py-3 border border-slate-200/60 dark:border-white/5 shadow-sm">
              <div className="flex gap-1.5 items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 pb-4 pt-3 border-t border-slate-200/60 dark:border-white/10 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl shrink-0">
        {/* Attachments Preview */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {attachments.map((att, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 text-xs shadow-sm max-w-[200px]">
                {att.type === 'image' ? (
                  att.dataUrl ? <img src={att.dataUrl} alt="preview" className="w-5 h-5 object-cover rounded" /> : <ImageIcon className="w-4 h-4 text-blue-500" />
                ) : (
                  <FileIcon className="w-4 h-4 text-red-500" />
                )}
                <span className="truncate flex-1 text-slate-700 dark:text-slate-300 font-medium">{att.file.name}</span>
                <button
                  onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                  className="text-slate-400 hover:text-red-500"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex gap-2 items-end">
          <input 
            type="file" 
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,image/png,image/jpeg,image/webp"
            className="hidden"
            multiple
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="h-10 w-10 shrink-0 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/60 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-500 shadow-sm flex items-center justify-center transition-all"
            title="Attach images or PDFs"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Ask me anything..."
            disabled={loading}
            className="h-10 rounded-xl border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-slate-900/60 text-sm focus-visible:ring-2 focus-visible:ring-indigo-500/30 focus-visible:border-indigo-500/50 flex-1"
          />
          <button
            onClick={handleSend}
            disabled={loading || (!input.trim() && attachments.length === 0)}
            className="h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/20 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
